import clsx from "clsx";
import trim from "lodash/trim";
import React, { lazy, Suspense, useEffect, useState } from "react";
import { useDispatch, useSelector as useReduxSelector } from "react-redux";
import InstantFeedback from "web/react/components/instant-feedback/instant-feedback";
import LoadingSpinner from "web/react/components/loading-spinner/loading-spinner";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import { useClickTracker } from "web/react/hooks/use-click-tracker/use-click-tracker";
import {
    startWhishlistAnimation,
    stopFetchingWishlist,
    stopWhishlistAnimation,
} from "web/redux/ducks/app-header-bar";
import { ReduxStoreState } from "web/redux/store";
import { gettext } from "web/script/modules/django-i18n";
import environment from "web/script/modules/environment";
import requester from "web/script/modules/requester";
import { canUseDOM } from "web/script/utils/browser/can-use-dom";
import { AccountLink } from "./account-links";
// eslint-disable-next-line css-modules/no-unused-class
import styles from "./account-links.module.css";

const LEGACYHeartAnimation = lazy(
    () =>
        import(
            "web/react/components/lottie-animations/LEGACY-heart-animation/LEGACY-heart-animation"
        )
);

const HeartAnimation = lazy(
    () => import("web/react/components/lottie-animations/heart-animation/heart-animation")
);

export default function WishListLink({
    url,
    hasItemsInWishlist,
}: AccountLink & { hasItemsInWishlist: boolean }): JSX.Element {
    const dispatch = useDispatch();
    const { isWhishlistAnimating, fetchWishlist } = useReduxSelector((state: ReduxStoreState) => {
        return state.appHeaderBarReducer;
    });
    const [hasSavedItems, setHasSavedItems] = useState(hasItemsInWishlist);
    const onClick = useClickTracker("nav", "clicked", trim(url as string, "/"));

    const showNewIconDesign = environment.getFeature("cw_pdp_redesign");

    useEffect(() => {
        async function checkWishList(): Promise<void> {
            try {
                const response = await requester.get("/services/pins/?get_all_pins=true");
                if (response.success) {
                    setHasSavedItems(response.has_pins);
                    dispatch(stopFetchingWishlist());
                }
            } catch (error) {
                console.error(error);
            }
        }

        if (!hasSavedItems || fetchWishlist) checkWishList();
    }, [fetchWishlist, hasSavedItems, isWhishlistAnimating, dispatch]);

    return (
        <div className={clsx(styles.glyph, "hidden-tablet")}>
            <InstantFeedback
                message={
                    // Item saved to your Wishlist!You'll get an alert when the price drops.
                    gettext("instant_feedback.saved")
                }
                area="heart"
                direction="bottom"
            >
                <a className={styles.icon} href={url} onClick={onClick}>
                    <div className={clsx(styles.iconLink, styles.noFill)}>
                        <SVGIcon
                            className={clsx(styles.icon, {
                                [styles.hasWishLyst]: hasSavedItems,
                            })}
                            name={hasSavedItems ? "heart-pressed" : "heart-unpressed"}
                        />
                        {canUseDOM() && isWhishlistAnimating && (
                            <Suspense fallback={<LoadingSpinner />}>
                                {showNewIconDesign ? (
                                    <div className={styles.emoAnimatedHeart}>
                                        <HeartAnimation
                                            onComplete={() => {
                                                dispatch(stopWhishlistAnimation());
                                            }}
                                            onAnimationStart={() => {
                                                dispatch(startWhishlistAnimation());
                                            }}
                                        />
                                    </div>
                                ) : (
                                    <div className={styles.animatedHeart}>
                                        <LEGACYHeartAnimation
                                            onComplete={() => {
                                                dispatch(stopWhishlistAnimation());
                                            }}
                                            onAnimationStart={() => {
                                                dispatch(startWhishlistAnimation());
                                            }}
                                        />
                                    </div>
                                )}
                            </Suspense>
                        )}
                    </div>
                    <div className={styles.iconText}>
                        {
                            // Wishlist
                            gettext("header.wishlist")
                        }
                    </div>
                </a>
            </InstantFeedback>
        </div>
    );
}
